/**
 * <pour-product-tabs> — tab switching for the multi-product Pour Configurator.
 * Panels are eagerly rendered server-side (Section Rendering API can't change
 * which product section.settings resolves to); switching is a [hidden] flip
 * + pause/resume of the inactive viewer. Cache key suffixed with block.id so
 * two panels of the same product cache independently.
 */
class PourProductTabs extends HTMLElement {
  connectedCallback() {
    this.#panels = [...this.querySelectorAll('[data-pour-panel]')];

    // Bridge for Horizon's broken multi-block price updater: its lookup by
    // data-block-id misses because the fetched PDP HTML has its own
    // (different) block id. Replace the panel's [ref="priceContainer"]
    // from the response's first match. Bound regardless of panel count.
    this.#section = this.closest('.shopify-section');
    if (this.#section) {
      this.#section.addEventListener('variant:update', this.#onVariantUpdate);
    }

    if (this.#panels.length <= 1) return;

    // Scope to [data-target-panel] — swatch picker uses role="tab" too and
    // we'd clobber its aria-selected.
    this.#tabs = [...this.querySelectorAll('[data-target-panel]')];
    this.#tabs.forEach((tab) => {
      // Name + thumbnail hydrated from the matching panel's data-*: Liquid
      // section.blocks resolves reference settings unreliably as raw handles
      // or nil, so we render them inside the block's own context and pipe
      // across via DOM. Target the inner name/img nodes — NOT the button's
      // textContent, which would wipe the tile's image + structure.
      const panel = this.#panels.find((p) => p.id === tab.dataset.targetPanel);
      const label = panel?.dataset.blockLabel;
      const thumb = panel?.dataset.blockThumb;

      const nameEl = tab.querySelector('[data-rail-name]');
      if (label) {
        if (nameEl) nameEl.textContent = label;
        else tab.textContent = label; // fallback if the tile markup changes
      }

      const imgEl = tab.querySelector('[data-rail-img]');
      if (imgEl && thumb) {
        imgEl.src = thumb;
        if (label) imgEl.alt = label;
        imgEl.removeAttribute('hidden');
      }

      tab.addEventListener('click', this.#onClick);
      tab.addEventListener('keydown', this.#onKey);
    });
  }

  disconnectedCallback() {
    this.#tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.#onClick);
      tab.removeEventListener('keydown', this.#onKey);
    });
    this.#section?.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  #panels = [];
  #tabs = [];
  #section = null;

  #onVariantUpdate = (event) => {
    const html = event?.detail?.data?.html;
    if (!html) return;
    const target = event.target;
    const panel = target instanceof Element ? target.closest('[data-pour-panel]') : null;
    const scope = panel || this.#section;
    // The panel's product-price carries data-product-id so we can lift the
    // matching node out of a multi-block response — a naive "first match"
    // approach could plug a sibling product's price into this panel.
    const myProductPrice = scope.querySelector('product-price[data-product-id]');
    const myContainer = myProductPrice?.querySelector('[ref="priceContainer"]');
    if (!myContainer) return;
    const productId = myProductPrice.dataset.productId;
    const candidate = productId
      ? html.querySelector(`product-price[data-product-id="${productId}"] [ref="priceContainer"]`)
      : html.querySelector('product-price [ref="priceContainer"]');
    if (!candidate) return;
    myContainer.replaceWith(candidate.cloneNode(true));
  };

  #onClick = (event) => {
    const targetId = event.currentTarget.dataset.targetPanel;
    if (!targetId) return;
    this.#activate(targetId);
  };

  #onKey = (event) => {
    const targetIndex = this.#panels.findIndex(
      (p) => p.id === event.currentTarget.dataset.targetPanel
    );
    if (targetIndex < 0) return;

    let nextIndex = targetIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (targetIndex + 1) % this.#panels.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (targetIndex - 1 + this.#panels.length) % this.#panels.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#panels.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.#activate(this.#panels[nextIndex].id, { focus: true });
  };

  #activate(targetPanelId, { focus = false } = {}) {
    const targetPanel = this.#panels.find((p) => p.id === targetPanelId);
    if (!targetPanel) return;

    this.#panels.forEach((panel) => {
      const isTarget = panel === targetPanel;
      panel.toggleAttribute('hidden', !isTarget);
      const viewer = panel.querySelector('bunny-3d-viewer');
      if (isTarget) viewer?.resume?.();
      else viewer?.pause?.();
    });

    let activeTab = null;
    this.#tabs.forEach((tab) => {
      const isTarget = tab.dataset.targetPanel === targetPanelId;
      tab.setAttribute('aria-selected', isTarget ? 'true' : 'false');
      tab.setAttribute('tabindex', isTarget ? '0' : '-1');
      if (isTarget) activeTab = tab;
    });

    // Mobile strip overflows horizontally — nudge the active tab into view
    // so its underline isn't clipped. `nearest` no-ops if already visible.
    activeTab?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });

    if (focus) activeTab?.focus();
  }
}

customElements.define('pour-product-tabs', PourProductTabs);
